import React, { useEffect, useRef, useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useIndonesiaV2Language } from "./indonesiaV2/indonesiaV2Language";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaderData {
  dob: string;
  role: string;
}

interface MemberData {
  name: string;
  dob: string;
  role: string;
}

const emptyLeader = (): LeaderData => ({ dob: "", role: "" });
const emptyMember = (): MemberData => ({ name: "", dob: "", role: "" });

const MAX_MEMBERS = 20;

// ─── Bilingual copy (English / Bahasa Indonesia) ──────────────────────────────

const COPY = {
  en: {
    engineTag: "Team Energy Engine",
    pageTitle: "Matescan Group",
    pageSubtitle: "How your team's energy fits together",
    leaderTitle: "Team Leader (You)",
    leaderRolePlaceholder: "e.g. Team Lead, Founder",
    memberTitle: "Member",
    memberName: "Name",
    memberNamePlaceholder: "Enter name",
    dateOfBirth: "Date of Birth",
    dobPlaceholder: "DD / MM / YYYY",
    role: "Role",
    rolePlaceholder: "e.g. Designer, Manager",
    yourRole: "Your Role",
    currentSituation: "Current Situation",
    currentSituationPlaceholder: "What's going on with the team right now?",
    yourGoal: "Your Goal",
    yourGoalPlaceholder: "What do you want to happen?",
    addMember: "+ Add Another Member",
    submit: "👥 Run Matescan Group",
    footerNote: "a gentle read on your team's energy — not a verdict on anyone",
    errLeaderDob: "Your date of birth is required.",
    errMembers: "Every team member needs a name and date of birth.",
    errRunFailed: "Failed to run scan. Please try again.",
    errGeneric: "Something went wrong. Please try again.",
    remove: "Remove",
    loadingSteps: [
      { label: "Reading the room's energy...", icon: "✧" },
      { label: "Mapping how everyone connects...", icon: "◈" },
      { label: "Finding where the team clicks...", icon: "◉" },
      { label: "Shaping today's advice...", icon: "☉" },
      { label: "Writing your team reading...", icon: "🕊" },
    ],
    loadingTitle: "Reading Your Team's Energy",
    loadingTag: "Matescan Group",
  },
  id: {
    engineTag: "Mesin Energi Tim",
    pageTitle: "Matescan Grup",
    pageSubtitle: "Bagaimana energi tim Anda saling cocok",
    leaderTitle: "Pemimpin Tim (Anda)",
    leaderRolePlaceholder: "cth. Pemimpin Tim, Pendiri",
    memberTitle: "Anggota",
    memberName: "Nama",
    memberNamePlaceholder: "Masukkan nama",
    dateOfBirth: "Tanggal Lahir",
    dobPlaceholder: "TGL / BLN / THN",
    role: "Peran",
    rolePlaceholder: "cth. Desainer, Manajer",
    yourRole: "Peran Anda",
    currentSituation: "Situasi Saat Ini",
    currentSituationPlaceholder: "Apa yang sedang terjadi dengan tim saat ini?",
    yourGoal: "Tujuan Anda",
    yourGoalPlaceholder: "Apa yang Anda inginkan terjadi?",
    addMember: "+ Tambah Anggota Lain",
    submit: "👥 Jalankan Matescan Grup",
    footerNote:
      "bacaan lembut tentang energi tim Anda — bukan vonis untuk siapa pun",
    errLeaderDob: "Tanggal lahir Anda wajib diisi.",
    errMembers: "Setiap anggota tim membutuhkan nama dan tanggal lahir.",
    errRunFailed: "Gagal menjalankan pemindaian. Silakan coba lagi.",
    errGeneric: "Terjadi kesalahan. Silakan coba lagi.",
    remove: "Hapus",
    loadingSteps: [
      { label: "Membaca energi ruangan...", icon: "✧" },
      { label: "Memetakan bagaimana semua terhubung...", icon: "◈" },
      { label: "Menemukan di mana tim cocok...", icon: "◉" },
      { label: "Menyusun saran hari ini...", icon: "☉" },
      { label: "Menulis hasil bacaan tim Anda...", icon: "🕊" },
    ],
    loadingTitle: "Membaca Energi Tim Anda",
    loadingTag: "Matescan Grup",
  },
} as const;

type Lang = keyof typeof COPY;
type Copy = (typeof COPY)[Lang];

// Converts a displayed DD/MM/YYYY value to the ISO YYYY-MM-DD format the backend validator requires.
const dobToIso = (value: string): string => {
  const parts = value.split("/");
  if (parts.length !== 3) return value;
  const [day, month, year] = parts;
  if (!day || !month || !year) return value;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// ─── Date of Birth formatting helpers (mirrors EnergyMatchDesign styling) ─────

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += "/";
    }
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2099) year = 2099;
    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }
  return value;
};

const DateOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  t: Copy;
}> = ({ value, onChange, required, t }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        } else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        ) {
          newCursorPos = 7;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <div>
      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
        {t.dateOfBirth}
        {required && <span className="ml-1 text-[#C084FC]">*</span>}
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder={t.dobPlaceholder}
        value={value}
        onChange={handleChange}
        onBlur={(e) => onChange(validateDateValue(e.target.value))}
        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
      />
    </div>
  );
};

// ─── Shared UI atoms (mirrors CheckMateForm styling) ──────────────────────────

const InputField: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ label, type = "text", value, onChange, placeholder, required }) => (
  <div>
    <label
      className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
      style={{ color: "#c8bfd1" }}
    >
      {label}
      {required && (
        <span className="ml-1" style={{ color: "#C084FC" }}>
          *
        </span>
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
      style={{
        background: "#1a131f",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#f7f3fa",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#C084FC";
        e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  </div>
);

const LeaderBlock: React.FC<{
  data: LeaderData;
  onChange: (field: keyof LeaderData, value: string) => void;
  t: Copy;
}> = ({ data, onChange, t }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
        style={{ background: "linear-gradient(135deg,#C084FC,#6D4C92)" }}
      >
        ★
      </span>
      <h3
        className="text-xs font-semibold tracking-[0.15em] uppercase"
        style={{ color: "#c8bfd1" }}
      >
        {t.leaderTitle}
      </h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <DateOfBirthField
        value={data.dob}
        onChange={(v) => onChange("dob", v)}
        required
        t={t}
      />
      <InputField
        label={t.yourRole}
        value={data.role}
        onChange={(v) => onChange("role", v)}
        placeholder={t.leaderRolePlaceholder}
      />
    </div>
  </div>
);

const MemberBlock: React.FC<{
  idx: number;
  data: MemberData;
  onChange: (field: keyof MemberData, value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  t: Copy;
}> = ({ idx, data, onChange, onRemove, canRemove, t }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span
          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
          style={{ background: "linear-gradient(135deg,#7DD3FC,#6D4C92)" }}
        >
          {idx + 1}
        </span>
        <h3
          className="text-xs font-semibold tracking-[0.15em] uppercase"
          style={{ color: "#c8bfd1" }}
        >
          {t.memberTitle} {idx + 1}
        </h3>
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full transition-colors"
          style={{ color: "#ff8a9b" }}
        >
          {t.remove}
        </button>
      )}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <InputField
        label={t.memberName}
        value={data.name}
        onChange={(v) => onChange("name", v)}
        placeholder={t.memberNamePlaceholder}
        required
      />
      <DateOfBirthField
        value={data.dob}
        onChange={(v) => onChange("dob", v)}
        required
        t={t}
      />
      <InputField
        label={t.role}
        value={data.role}
        onChange={(v) => onChange("role", v)}
        placeholder={t.rolePlaceholder}
      />
    </div>
  </div>
);

// ─── Loading Screen (mirrors CheckMateForm's LoadingScreen) ───────────────────

const LoadingScreen: React.FC<{ t: Copy }> = ({ t }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const steps = t.loadingSteps;

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = prev + 1;
        if (next >= steps.length) {
          clearInterval(timerRef.current!);
          return prev;
        }
        return next;
      });
    }, 420);
    return () => clearInterval(timerRef.current!);
  }, [steps.length]);

  const currentStep = steps[activeIdx];

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark] flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.07,
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.1,
          }}
        />
      </div>

      <div className="relative mb-10">
        <svg
          width="112"
          height="112"
          viewBox="0 0 112 112"
          className="animate-spin"
          style={{ animationDuration: "6s" }}
        >
          <circle
            cx="56"
            cy="56"
            r="48"
            fill="none"
            stroke="#C084FC"
            strokeWidth="1"
            strokeDasharray="5 7"
            opacity="0.35"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C084FC]/20 to-[#6D4C92]/20 border border-[#C084FC]/30 flex items-center justify-center">
            <span className="text-xl text-[#d9ccff]">👥</span>
          </div>
        </div>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-light tracking-wide mb-1 text-center"
        style={{ color: "#c8bfd1" }}
      >
        {t.loadingTitle}
      </motion.h2>
      <p
        className="text-xs tracking-[0.18em] uppercase mb-10 text-center"
        style={{ color: "#a89bb5" }}
      >
        {t.loadingTag}
      </p>

      <div className="w-full max-w-xs min-h-[52px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#221a29] border border-[#C084FC]/30 shadow-sm"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#C084FC] to-[#6D4C92]">
              <span className="text-white text-sm">{currentStep.icon}</span>
            </div>
            <span className="text-sm font-medium" style={{ color: "#c8bfd1" }}>
              {currentStep.label}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-6">
        {steps.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              background: i <= activeIdx ? "#C084FC" : "rgba(255,255,255,0.12)",
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const MatescanForm: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const lang: Lang = useIndonesiaV2Language();
  const t = useLaneCopy("msform", COPY);

  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId") || undefined;

  const [leader, setLeader] = useState<LeaderData>(emptyLeader());
  const [members, setMembers] = useState<MemberData[]>([
    emptyMember(),
    emptyMember(),
  ]);
  const [context, setContext] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLeader = (field: keyof LeaderData, value: string) => {
    setLeader((prev) => ({ ...prev, [field]: value }));
  };

  const updateMember = (
    idx: number,
    field: keyof MemberData,
    value: string,
  ) => {
    setMembers((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)),
    );
  };

  const addMember = () => {
    if (members.length >= MAX_MEMBERS) return;
    setMembers((prev) => [...prev, emptyMember()]);
  };

  const removeMember = (idx: number) => {
    setMembers((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!leader.dob) {
      setError(t.errLeaderDob);
      return;
    }
    if (members.some((m) => !m.name || !m.dob)) {
      setError(t.errMembers);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        leader: { ...leader, dob: dobToIso(leader.dob) },
        members: members.map((m) => ({ ...m, dob: dobToIso(m.dob) })),
        context,
        goal,
        userId: localStorage.getItem("userId") || undefined,
        categoryId,
      };
      const response = await api.post("/backend/matescan/scan", payload);
      if (response.data?.success) {
        sessionStorage.setItem("matescanScan", JSON.stringify(response.data));
        navigate("/matescan/result");
      } else {
        setLoading(false);
        const errs: string[] = response.data?.errors;
        setError(
          Array.isArray(errs) && errs.length > 0
            ? errs.join(" · ")
            : response.data?.message || t.errRunFailed,
        );
      }
    } catch (err: any) {
      setLoading(false);
      const errs: string[] = err?.response?.data?.errors;
      setError(
        Array.isArray(errs) && errs.length > 0
          ? errs.join(" · ")
          : err?.response?.data?.message || t.errGeneric,
      );
    }
  };

  if (loading) return <LoadingScreen t={t} />;

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: "#a89bb5" }}
              >
                {t.engineTag}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>
            <h1
              className="text-xl sm:text-2xl md:text-3xl font-light tracking-wide"
              style={{ color: "#c8bfd1" }}
            >
              {t.pageTitle}
            </h1>
            <p className="text-xs sm:text-sm mt-2" style={{ color: "#a89bb5" }}>
              {t.pageSubtitle}
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border"
            style={{ background: "#1a131f", borderColor: "rgba(255,255,255,0.1)" }}
          >
            <div className="space-y-6 sm:space-y-8">
              <LeaderBlock data={leader} onChange={updateLeader} t={t} />

              <div
                className="border-t pt-6 sm:pt-8 space-y-6 sm:space-y-8"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
              >
                {members.map((member, idx) => (
                  <MemberBlock
                    key={idx}
                    idx={idx}
                    data={member}
                    onChange={(field, value) => updateMember(idx, field, value)}
                    onRemove={() => removeMember(idx)}
                    canRemove={members.length > 1}
                    t={t}
                  />
                ))}

                {members.length < MAX_MEMBERS && (
                  <button
                    type="button"
                    onClick={addMember}
                    className="w-full rounded-xl py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border transition-colors"
                    style={{
                      color: "#d9ccff",
                      borderColor: "#C084FC",
                      background: "#221a29",
                    }}
                  >
                    {t.addMember}
                  </button>
                )}
              </div>

              <div
                className="border-t pt-6 sm:pt-8 grid grid-cols-1 gap-4"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
              >
                <InputField
                  label={t.currentSituation}
                  value={context}
                  onChange={setContext}
                  placeholder={t.currentSituationPlaceholder}
                />
                <InputField
                  label={t.yourGoal}
                  value={goal}
                  onChange={setGoal}
                  placeholder={t.yourGoalPlaceholder}
                />
              </div>
            </div>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 text-red-500 text-xs text-center font-medium"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Submit */}
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              className="w-full rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
              style={{
                background: "#a78bff", color: "#0f0a14", boxShadow: "0 0 22px rgba(167,139,255,0.35)", borderRadius: 14,
              }}
            >
              {t.submit}
            </button>
          </div>

          <div
            className="text-center mt-6 text-[10px] sm:text-[11px] tracking-wide"
            style={{ color: "#a89bb5" }}
          >
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            {t.footerNote}
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatescanForm;
